import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, Lock, Menu, Search, X } from 'lucide-react';
import { NAV_GROUPS } from '@/config/navigation';
import { MOBILE_BREAKPOINT, SIDEBAR_EXPANDED_WIDTH, SIDEBAR_RAIL_WIDTH } from '@/config/layout';
import UserMenu from '@/components/UserMenu';
import { COMMAND_PALETTE_OPEN_EVENT } from '@/components/CommandPalette';

const COLLAPSED_KEY = 'sidebarCollapsed';
const COLLAPSED_GROUPS_KEY = 'sidebarCollapsedGroups';

function readCollapsedGroups() {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// Computed once, synchronously, before Sidebar's first paint — see the
// useState initializer below for why.
function readInitialCollapsed() {
  const collapsed = localStorage.getItem(COLLAPSED_KEY) === 'true';
  // Set the CSS var here too, not only in the effect. AppLayout's content
  // wrapper reads --sidebar-width on first paint via a CSS fallback
  // (var(--sidebar-width, 260px)); if the persisted state is "collapsed"
  // (64px) but this only got set in a useEffect (which runs after first
  // paint), content briefly renders margined 260px then snaps to 64px — a
  // real, visible one-frame layout jump. Setting it here, during the
  // initializer (which runs synchronously in the render phase, before the
  // browser paints), eliminates that flash.
  document.documentElement.style.setProperty(
    '--sidebar-width',
    `${collapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH}px`
  );
  return collapsed;
}

export default function Sidebar() {
  const { theme } = useTheme();
  const { hasActiveSubscription, isAuthenticated } = useAuth();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(readInitialCollapsed);
  const [collapsedGroups, setCollapsedGroups] = useState(readCollapsedGroups);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSubscribed = hasActiveSubscription ? hasActiveSubscription() : false;
  const width = collapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  // Keep the CSS var in sync on every subsequent toggle (the initializer
  // above only covers the very first render).
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${width}px`);
  }, [width]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const toggleGroup = useCallback((group) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [group]: !prev[group] };
      localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Close the mobile drawer on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Cmd+K/Ctrl+K had zero discoverable affordance — nobody was told the
  // palette exists. This button is the fix: dispatches the same open event
  // CommandPalette listens for.
  const openCommandPalette = useCallback(() => {
    window.dispatchEvent(new Event(COMMAND_PALETTE_OPEN_EVENT));
  }, []);

  return (
    <>
      {/* Mobile hamburger — only visible under the mobile breakpoint (see
          media query below). Positioned clear of the existing fixed top-left
          logo, which now lives in this component's own header instead. */}
      <button
        onClick={() => setMobileOpen((o) => !o)}
        className="sidebar-mobile-toggle"
        aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
        style={{
          position: 'fixed',
          top: 12,
          left: 16,
          zIndex: 45,
          width: 40,
          height: 40,
          display: 'none',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.cardBg,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: 8,
          color: theme.textPrimary,
          cursor: 'pointer',
        }}
      >
        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Mobile scrim */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="sidebar-scrim"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 29 }}
        />
      )}

      <aside
        className={`app-sidebar${mobileOpen ? ' app-sidebar--open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: `${width}px`,
          background: theme.cardBg,
          borderRight: `1px solid ${theme.borderColor}`,
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.2s ease, transform 0.2s ease',
        }}
      >
        {/* Header — logo (consolidated from AppLayout's old standalone fixed
            element) + whole-sidebar collapse toggle. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.borderColor}`,
            flexShrink: 0,
          }}
        >
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <img src="/gammaqc-logo.png" alt="Gamma QC" style={{ width: 32, height: 32, borderRadius: 6 }} />
            {!collapsed && (
              <span style={{ color: theme.textPrimary, fontWeight: 700, fontSize: 14, fontFamily: 'Space Grotesk, sans-serif' }}>
                Gamma QC
              </span>
            )}
          </Link>
          {!collapsed && (
            <button
              onClick={toggleCollapsed}
              aria-label="Collapse sidebar"
              className="sidebar-desktop-collapse"
              style={{ background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', padding: 4 }}
            >
              <ChevronsLeft size={16} />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={toggleCollapsed}
            aria-label="Expand sidebar"
            className="sidebar-desktop-collapse"
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.textSecondary,
              cursor: 'pointer',
              padding: '8px 0',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ChevronsRight size={16} />
          </button>
        )}

        {/* Search / command palette trigger — previously the palette had zero
            discoverable affordance (Cmd+K only, nobody told). */}
        <button
          onClick={openCommandPalette}
          title="Search (Ctrl+K)"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            justifyContent: collapsed ? 'center' : 'flex-start',
            margin: collapsed ? '8px' : '8px 12px',
            padding: collapsed ? '8px' : '8px 10px',
            background: theme.background,
            border: `1px solid ${theme.borderColor}`,
            borderRadius: 8,
            color: theme.textSecondary,
            cursor: 'pointer',
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          <Search size={15} style={{ flexShrink: 0 }} />
          {!collapsed && (
            <>
              <span style={{ flex: 1, textAlign: 'left' }}>Search</span>
              <span style={{ fontSize: 11, opacity: 0.7 }}>Ctrl+K</span>
            </>
          )}
        </button>

        {/* Nav groups */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {NAV_GROUPS.map((g) => {
            const isCollapsed = !!collapsedGroups[g.group];
            return (
              <div key={g.group} style={{ marginBottom: 4 }}>
                {!collapsed && (
                  <button
                    onClick={() => toggleGroup(g.group)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 16px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: theme.textSecondary,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    <span>{g.group}</span>
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}
                {(collapsed || !isCollapsed) && (
                  <div>
                    {g.items.map((item) => {
                      const Icon = item.icon;
                      // External items never match a router path (they're a
                      // full URL) and are never premium-gated.
                      const active = !item.external && location.pathname === item.path;
                      const locked = !item.external && item.premium && !isSubscribed;
                      const linkStyle = {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: collapsed ? '10px 0' : '8px 16px 8px 24px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        textDecoration: 'none',
                        color: active ? theme.primary : theme.textPrimary,
                        background: active ? `${theme.primary}12` : 'transparent',
                        borderLeft: active ? `3px solid ${theme.primary}` : '3px solid transparent',
                        fontSize: 13,
                        fontWeight: active ? 700 : 500,
                      };
                      const inner = (
                        <>
                          <Icon size={16} style={{ flexShrink: 0 }} />
                          {!collapsed && (
                            <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.label}
                            </span>
                          )}
                          {!collapsed && locked && <Lock size={12} style={{ color: theme.textSecondary, flexShrink: 0 }} />}
                        </>
                      );
                      if (item.external) {
                        return (
                          <a
                            key={item.path}
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={collapsed ? item.label : undefined}
                            style={linkStyle}
                          >
                            {inner}
                          </a>
                        );
                      }
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          title={collapsed ? item.label : undefined}
                          style={linkStyle}
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Account controls — UserMenu.js was fully built but never mounted
            anywhere in the app; there was previously no sign-out affordance
            at all. It lives here, in the sidebar footer, rather than
            competing for space in the already-crowded top-right chrome
            (theme toggle + OraTopBar). */}
        {isAuthenticated && !collapsed && (
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${theme.borderColor}`, flexShrink: 0 }}>
            <UserMenu />
          </div>
        )}
      </aside>

      <style>{`
        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          .sidebar-mobile-toggle {
            display: flex !important;
          }
          .sidebar-desktop-collapse {
            display: none !important;
          }
          .app-sidebar {
            transform: translateX(-100%);
            width: ${SIDEBAR_EXPANDED_WIDTH}px !important;
          }
          .app-sidebar--open {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}
